import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { 
  Customer, 
  CustomerListResponse, 
  CreateCustomerRequest, 
  UpdateCustomerRequest,
  CustomerSearchParams,
  CustomerStatistics
} from '../models/CustomerModel';
import * as CustomerCRUD from './CustomerCRUD';

interface CustomerState {
  customers: Customer[];
  loading: boolean;
  error: string | null;
  totalCount: number;
  statistics: CustomerStatistics | null;
  selectedCustomers: string[];
}

const initialState: CustomerState = {
  customers: [],
  loading: false,
  error: null,
  totalCount: 0,
  statistics: null,
  selectedCustomers: [],
};

// Async thunks
export const fetchCustomers = createAsyncThunk(
  'customers/fetchCustomers',
  async (params?: CustomerSearchParams) => {
    const response = await CustomerCRUD.getCustomers(params);
    return response;
  }
);

export const fetchCustomerById = createAsyncThunk(
  'customers/fetchCustomerById',
  async (id: string) => {
    const response = await CustomerCRUD.getCustomerById(id);
    return response;
  }
);

export const createCustomer = createAsyncThunk(
  'customers/createCustomer',
  async (customerData: CreateCustomerRequest) => {
    const response = await CustomerCRUD.createCustomer(customerData);
    return response;
  }
);

export const updateCustomer = createAsyncThunk(
  'customers/updateCustomer',
  async ({ id, customerData }: { id: string; customerData: UpdateCustomerRequest }) => {
    const response = await CustomerCRUD.updateCustomer(id, customerData);
    return response;
  }
);

export const deleteCustomer = createAsyncThunk(
  'customers/deleteCustomer',
  async (id: string) => {
    await CustomerCRUD.deleteCustomer(id);
    return id;
  }
);

export const searchCustomers = createAsyncThunk(
  'customers/searchCustomers',
  async (params: CustomerSearchParams) => {
    const response = await CustomerCRUD.searchCustomers(params);
    return response;
  }
);

export const fetchCustomerStatistics = createAsyncThunk(
  'customers/fetchCustomerStatistics',
  async () => {
    const response = await CustomerCRUD.getCustomerStatistics();
    return response;
  }
);

export const bulkUpdateCustomers = createAsyncThunk(
  'customers/bulkUpdateCustomers',
  async (data: { customer_ids: string[]; status?: 'active' | 'inactive' | 'pending' | 'blocked'; priority?: 'low' | 'medium' | 'high' | 'vip'; company_ids?: string[] }) => {
    const response = await CustomerCRUD.bulkUpdateCustomers(data);
    return response;
  }
);

// Slice
const customerSlice = createSlice({
  name: 'customers',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    selectCustomer: (state, action: PayloadAction<string>) => {
      const customerId = action.payload;
      if (state.selectedCustomers.includes(customerId)) {
        state.selectedCustomers = state.selectedCustomers.filter(id => id !== customerId);
      } else {
        state.selectedCustomers.push(customerId);
      }
    },
    selectAllCustomers: (state, action: PayloadAction<boolean>) => {
      if (action.payload) {
        state.selectedCustomers = state.customers.map(customer => customer.id);
      } else {
        state.selectedCustomers = [];
      }
    },
    clearSelectedCustomers: (state) => {
      state.selectedCustomers = [];
    },
  },
  extraReducers: (builder) => {
    // fetchCustomers
    builder
      .addCase(fetchCustomers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCustomers.fulfilled, (state, action) => {
        state.loading = false;
        // Handle both array response and paginated response
        if (Array.isArray(action.payload)) {
          state.customers = action.payload;
          state.totalCount = action.payload.length;
        } else {
          state.customers = action.payload.results || [];
          state.totalCount = action.payload.count || 0;
        }
      })
      .addCase(fetchCustomers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch customers';
      });

    // fetchCustomerById
    builder
      .addCase(fetchCustomerById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCustomerById.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.customers.findIndex(customer => customer.id === action.payload.id);
        if (index !== -1) {
          state.customers[index] = action.payload;
        } else {
          state.customers.push(action.payload);
        }
      })
      .addCase(fetchCustomerById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch customer';
      });

    // createCustomer
    builder
      .addCase(createCustomer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCustomer.fulfilled, (state, action) => {
        state.loading = false;
        state.customers.unshift(action.payload);
        state.totalCount += 1;
      })
      .addCase(createCustomer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create customer';
      });

    // updateCustomer
    builder
      .addCase(updateCustomer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCustomer.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.customers.findIndex(customer => customer.id === action.payload.id);
        if (index !== -1) {
          state.customers[index] = action.payload;
        }
      })
      .addCase(updateCustomer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to update customer';
      });

    // deleteCustomer
    builder
      .addCase(deleteCustomer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteCustomer.fulfilled, (state, action) => {
        state.loading = false;
        state.customers = state.customers.filter(customer => customer.id !== action.payload);
        state.selectedCustomers = state.selectedCustomers.filter(id => id !== action.payload);
        state.totalCount = Math.max(0, state.totalCount - 1);
      })
      .addCase(deleteCustomer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to delete customer';
      });

    // searchCustomers
    builder
      .addCase(searchCustomers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(searchCustomers.fulfilled, (state, action) => {
        state.loading = false;
        // Handle both array response and paginated response
        if (Array.isArray(action.payload)) {
          state.customers = action.payload;
          state.totalCount = action.payload.length;
        } else {
          state.customers = action.payload.results || [];
          state.totalCount = action.payload.count || 0;
        }
      })
      .addCase(searchCustomers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to search customers';
      });

    // fetchCustomerStatistics
    builder
      .addCase(fetchCustomerStatistics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCustomerStatistics.fulfilled, (state, action) => {
        state.loading = false;
        state.statistics = action.payload;
      })
      .addCase(fetchCustomerStatistics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch customer statistics';
      });

    // bulkUpdateCustomers
    builder
      .addCase(bulkUpdateCustomers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(bulkUpdateCustomers.fulfilled, (state) => {
        state.loading = false;
        // Refresh the customers list after bulk update
        // You might want to refetch the customers here
      })
      .addCase(bulkUpdateCustomers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to bulk update customers';
      });
  },
});

export const { 
  clearError, 
  selectCustomer, 
  selectAllCustomers, 
  clearSelectedCustomers 
} = customerSlice.actions;

// Selectors
export const selectCustomers = (state: any) => state.customer.customers;
export const selectCustomersLoading = (state: any) => state.customer.loading;
export const selectCustomersError = (state: any) => state.customer.error;
export const selectCustomersTotalCount = (state: any) => state.customer.totalCount;
export const selectSelectedCustomers = (state: any) => state.customer.selectedCustomers;
export const selectCustomerStatistics = (state: any) => state.customer.statistics;

export default customerSlice.reducer;
